import { useEffect, useCallback, useRef } from 'react';
import { useOnboardingStore } from '@/stores/onboardingStore';

const DRAFT_STORAGE_KEY = 'quentari_decision_draft';
const DRAFT_EXPIRY_HOURS = 24;

interface ClassificationSnapshot {
  decision_type: string;
  decision_subtype: string;
  confidence: number;
  reasoning: string;
  suggested_title: string;
}

interface DraftData {
  decisionType: string | null;
  decisionSubtype: string | null;
  trigger: string;
  stakes: string;
  previousAttempts: string;
  impactScore: number;
  urgencyScore: number;
  currentSubStep: number;
  currentStep: number;
  savedAt: number;
  /** Free-text description from Step 1 */
  userDescription?: string;
  /** Classification result snapshot for crash recovery */
  classification?: ClassificationSnapshot | null;
  /** Stable idempotency key for session creation */
  startFlowId?: string;
}

export function useDecisionDraft(workspaceId: string, isOpen: boolean) {
  const store = useOnboardingStore();
  const hasRestoredRef = useRef(false);
  
  // Check if draft is expired
  const isDraftExpired = useCallback((savedAt: number) => {
    const expiryTime = DRAFT_EXPIRY_HOURS * 60 * 60 * 1000;
    return Date.now() - savedAt > expiryTime;
  }, []);

  // Save draft to localStorage
  const saveDraft = useCallback((currentStep: number, extras?: { userDescription?: string; classification?: ClassificationSnapshot | null; startFlowId?: string }) => {
    const draft: DraftData = {
      decisionType: store.decisionType,
      decisionSubtype: store.decisionSubtype,
      trigger: store.trigger,
      stakes: store.stakes,
      previousAttempts: store.previousAttempts,
      impactScore: store.impactScore,
      urgencyScore: store.urgencyScore,
      currentSubStep: store.currentSubStep,
      currentStep,
      savedAt: Date.now(),
      userDescription: extras?.userDescription,
      classification: extras?.classification,
      startFlowId: extras?.startFlowId,
    };
    
    // Save if there's meaningful data (including description from step 1)
    if (draft.decisionType || draft.trigger || draft.userDescription) {
      localStorage.setItem(`${DRAFT_STORAGE_KEY}_${workspaceId}`, JSON.stringify(draft));
    }
  }, [store, workspaceId]);

  // Load draft from localStorage
  const loadDraft = useCallback((): { draft: DraftData | null; currentStep: number } => {
    try {
      const stored = localStorage.getItem(`${DRAFT_STORAGE_KEY}_${workspaceId}`);
      if (!stored) return { draft: null, currentStep: 1 };
      
      const draft: DraftData = JSON.parse(stored);
      
      if (isDraftExpired(draft.savedAt)) {
        clearDraft();
        return { draft: null, currentStep: 1 };
      }
      
      return { draft, currentStep: draft.currentStep };
    } catch {
      return { draft: null, currentStep: 1 };
    }
  }, [workspaceId, isDraftExpired]);

  // Clear draft from localStorage
  const clearDraft = useCallback(() => {
    localStorage.removeItem(`${DRAFT_STORAGE_KEY}_${workspaceId}`);
  }, [workspaceId]);

  // Restore draft to store
  const restoreDraft = useCallback((draft: DraftData) => {
    if (draft.decisionType) store.setDecisionArea(draft.decisionType);
    if (draft.decisionSubtype) store.setDecisionSubtype(draft.decisionSubtype);
    if (draft.trigger) store.setTrigger(draft.trigger);
    if (draft.stakes) store.setStakes(draft.stakes);
    if (draft.previousAttempts) store.setPreviousAttempts(draft.previousAttempts);
    if (draft.impactScore || draft.urgencyScore) {
      store.setScores(draft.impactScore, draft.urgencyScore);
    }
    if (draft.currentSubStep) store.setSubStep(draft.currentSubStep);
  }, [store]);

  // Check if there's a valid draft
  const hasDraft = useCallback(() => {
    const { draft } = loadDraft();
    return draft !== null && (draft.decisionType || draft.trigger || draft.userDescription);
  }, [loadDraft]);

  // Reset the restored flag when sheet closes
  useEffect(() => {
    if (!isOpen) {
      hasRestoredRef.current = false;
    }
  }, [isOpen]);

  return {
    saveDraft,
    loadDraft,
    clearDraft,
    restoreDraft,
    hasDraft,
    hasRestoredRef,
  };
}
